import React, { CSSProperties, Fragment } from 'react';
import { Manager, Popper, PopperArrowProps, Reference, RefHandler } from 'react-popper';
import { common } from '@kurtosys/ksys-app-template';
import { Placement } from '@popperjs/core';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import { KEYS } from '../../../shared/KeyboardKeys.js';
import { IIconProps } from '../Icon/models/index.js';
import { ITranslationContextValue } from '../Translate/models/index.js';
import Translate from '../Translate/Translate.js';
import { TranslationContext } from '../Translate/Translate.js';

import { ITooltipConfiguration, ITooltipProps, ITooltipState } from './models/index.js';
import ArrowIcon from './styledComponents/ArrowIcon.js';
import ClickLayer from './styledComponents/ClickLayer.js';
import TooltipCharacter from './styledComponents/TooltipCharacter.js';
import TooltipChildrenButton from './styledComponents/TooltipChildrenButton.js';
import TooltipContent from './styledComponents/TooltipContent.js';
import TooltipIcon from './styledComponents/TooltipIcon.js';
import TooltipIconButton from './styledComponents/TooltipIconButton.js';
import Wrapper from './styledComponents/Wrapper.js';
import { TOOLTIP_CATEGORY_KEY, TOOLTIP_COMPONENT_KEY } from './Tooltip.meta.js';

export class Tooltip extends ComponentBase<ITooltipProps, ITooltipState, ITooltipConfiguration> {
	categoryKey = TOOLTIP_CATEGORY_KEY;
	componentKey = TOOLTIP_COMPONENT_KEY;

	shown = false;
	translationContext: ITranslationContextValue | undefined;

	show = () => {
		this.shown = true;
	};

	hide = () => {
		this.shown = false;
	};

	onClick = (e: React.MouseEvent) => {
		const { preserveClickAction = false, trigger = 'click' } = this.props;
		if (!preserveClickAction) {
			e.stopPropagation();
		}
		if (trigger === 'click') {
			this.show();
		}
	};

	onMouseEnter = () => {
		const { trigger = 'click' } = this.props;
		if (trigger === 'hover') {
			this.show();
		}
	};

	onMouseLeave = () => {
		const { trigger = 'click' } = this.props;
		if (trigger === 'hover') {
			this.hide();
		}
	};

	onKeyDown(evt: React.KeyboardEvent) {
		if (evt.key === KEYS.ESCAPE) {
			this.hide();
		}
	}

	onBlur = () => {
		this.hide();
	};

	constructor(props: ITooltipProps) {
		super(props);

		makeObservable(this, {
			shown: observable,
			show: action,
			hide: action,
			onClick: action,
			onMouseEnter: action,
			onMouseLeave: action,
			onKeyDown: action,
			onBlur: action,
			tooltipButtonContent: computed,
			tooltipContent: computed,
		});
	}

	get tooltipButtonContent() {
		const { character, iconProps, buttonAccessibleText, children } = this.props;
		if (!common.commonUtils.isNullOrEmpty(character)) {
			return <TooltipCharacter>{character}</TooltipCharacter>;
		}
		const tooltipIconProps: IIconProps = iconProps || {
			asset: 'base.tooltip.defaultIcon',
		};
		tooltipIconProps.addAccessibleText = buttonAccessibleText || children ? false : true;
		return <TooltipIcon {...tooltipIconProps} />;
	}

	get tooltipContent() {
		let { content = '' } = this.props;

		if (typeof content === 'string') {
			if (this.translationContext) {
				content = this.translationContext.translate(content);
			}
			return <div dangerouslySetInnerHTML={{ __html: content }} />;
		}
		return content;
	}

	getButton(ref: RefHandler) {
		const { children, buttonAccessibleText } = this.props;
		if (!ref) {
			return null;
		}
		/**
		 * Tab index of 0 is to allow the element to receive focus for the keyDown event to be triggered
		 * This have been tested on Chrome, Safari, Opera, Edge, and IE as well (just for completeness)
		 */
		return (
			<div ref={ref}>
				{!common.commonUtils.isNullOrUndefined(children) ? (
					<TooltipChildrenButton
						onClick={this.onClick}
						onMouseEnter={this.onMouseEnter}
						onMouseLeave={this.onMouseLeave}
						onKeyDown={(evt: React.KeyboardEvent<Element>) => this.onKeyDown(evt)}
						onBlur={this.onBlur}
						aria-label={buttonAccessibleText}
						role="button"
						tabIndex={0}
					>
						{children}
					</TooltipChildrenButton>
				) : (
					<TooltipIconButton
						onClick={this.onClick}
						onMouseEnter={this.onMouseEnter}
						onMouseLeave={this.onMouseLeave}
						onKeyDown={(evt: React.KeyboardEvent<Element>) => this.onKeyDown(evt)}
						onBlur={this.onBlur}
						aria-label={buttonAccessibleText}
						role="button"
						tabIndex={0}
					>
						{this.tooltipButtonContent}
					</TooltipIconButton>
				)}
			</div>
		);
	}

	getContent(ref: RefHandler, style: CSSProperties, placement: Placement, arrowProps: PopperArrowProps) {
		const { showTooltipArrow = true } = this.props;
		return (
			<div data-selector="base-tooltip-tooltipContentWrapper" ref={ref} style={style} data-placement={placement}>
				<TooltipContent aria-live="polite">
					<Translate>{this.tooltipContent}</Translate>
					{showTooltipArrow && (
						<div
							data-selector="base-tooltip-arrowIconWrapper"
							ref={arrowProps.ref}
							style={arrowProps.style}
							aria-hidden={true}
						>
							<ArrowIcon />
						</div>
					)}
				</TooltipContent>
			</div>
		);
	}

	get hasContent(): boolean {
		const { content = '' } = this.props;
		return !common.commonUtils.isNullOrEmpty(content);
	}

	render() {
		const { className, position = 'right', trigger = 'click', fixedTooltip = false } = this.props;
		if (!this.hasContent) {
			return null;
		}
		return (
			<Wrapper className={className}>
				<Manager>
					<Reference>{({ ref }) => this.getButton(ref as RefHandler)}</Reference>
					{this.shown && (
						<Fragment>
							{trigger === 'click' && <ClickLayer onClick={this.hide} />}
							<TranslationContext.Consumer>
								{(translationContext) => {
									this.translationContext = translationContext;
									return null;
								}}
							</TranslationContext.Consumer>
							<Popper placement={position} strategy={fixedTooltip ? 'fixed' : 'absolute'}>
								{({ ref, style, placement, arrowProps }) =>
									this.getContent(ref as RefHandler, style, placement, arrowProps)
								}
							</Popper>
						</Fragment>
					)}
				</Manager>
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(Tooltip), TOOLTIP_CATEGORY_KEY, TOOLTIP_COMPONENT_KEY);
